import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CsvInput from "./CsvInput";

const STATUSES = ["off-plan", "under-construction", "ready", "completed"];
const PUBLISH = ["draft", "review", "published"];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">{label}</span>
      {children}
    </label>
  );
}

const empty = {
  slug: "", name: "", developer_slug: "", community: "", sub_community: "",
  city: "Dubai", emirate: "Dubai", status: "off-plan", handover: "",
  starting_price: "", currency: "AED", unit_types: [], unit_mix: "",
  payment_plan: "", amenities: [], nearby_landmarks: [],
  description: "", hero_image: "", gallery: [], brochure_url: "",
  floor_plans: [], latitude: "", longitude: "",
  seo_title: "", seo_description: "", faqs: [],
  publish_status: "published", featured: false,
};

export default function AdminProjectForm() {
  const { slug } = useParams();
  const isEdit = Boolean(slug);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [developers, setDevelopers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [faqDraft, setFaqDraft] = useState({ q: "", a: "" });

  useEffect(() => {
    api.get("/developers").then((r) => setDevelopers(r.data.items || []));
    if (isEdit) {
      api.get(`/projects/${slug}`, { params: { raw: true } }).then((r) => {
        const d = r.data;
        setForm({ ...empty, ...d, starting_price: d.starting_price ?? "", latitude: d.latitude ?? "", longitude: d.longitude ?? "" });
      });
    }
  }, [slug, isEdit]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    return submitWithStatus(form.publish_status || "published");
  };

  const submitWithStatus = async (status) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        publish_status: status,
        slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        starting_price: form.starting_price ? Number(form.starting_price) : null,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
      };
      if (isEdit) {
        await api.put(`/admin/projects/${slug}`, payload);
        toast.success(status === "published" ? "Project published" : "Draft saved");
      } else {
        await api.post(`/admin/projects`, payload);
        toast.success(status === "published" ? "Project published" : "Draft created");
      }
      setForm((s) => ({ ...s, publish_status: status }));
      navigate("/admin/projects");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = "Save failed";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw)) msg = raw.map((e) => `${(e.loc || []).slice(-1)}: ${e.msg}`).join(" · ");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-[#001588]";

  const statusPill = form.publish_status === "published"
    ? { label: "Live", cls: "bg-green-100 text-green-800 border-green-300" }
    : form.publish_status === "review"
      ? { label: "Under review", cls: "bg-amber-100 text-amber-800 border-amber-300" }
      : { label: "Draft", cls: "bg-neutral-100 text-neutral-700 border-neutral-300" };

  return (
    <form onSubmit={submit} className="p-10 max-w-5xl" data-testid="admin-project-form">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Inventory</div>
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-4xl">{isEdit ? "Edit project" : "New project"}</h1>
            <span className={`text-xs uppercase tracking-[0.15em] px-2.5 py-1 border ${statusPill.cls}`} data-testid="project-status-pill">
              {statusPill.label}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => submitWithStatus("draft")} disabled={saving} data-testid="admin-project-save-draft" className="px-5 py-3 border border-[#001588] text-[#001588] text-sm uppercase tracking-[0.2em] hover:bg-[#001588] hover:text-white transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save as draft"}
          </button>
          <button type="button" onClick={() => submitWithStatus("published")} disabled={saving} data-testid="admin-project-publish" className="px-6 py-3 bg-[#001588] text-white text-sm uppercase tracking-[0.2em] hover:bg-black disabled:opacity-60">
            {saving ? "Publishing…" : form.publish_status === "published" && isEdit ? "Update live" : "Publish"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white border border-[#E5E7EB] p-8">
        <Field label="Project name *"><input required data-testid="project-input-name" className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} /></Field>
        <Field label="Slug (auto if blank)"><input className={inputCls} data-testid="project-input-slug" value={form.slug} onChange={(e) => update("slug", e.target.value)} disabled={isEdit} /></Field>

        <Field label="Developer">
          <select className={inputCls} data-testid="project-input-developer" value={form.developer_slug || ""} onChange={(e) => update("developer_slug", e.target.value)}>
            <option value="">— Select developer —</option>
            {developers.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Community"><input className={inputCls} data-testid="project-input-community" value={form.community} onChange={(e) => update("community", e.target.value)} /></Field>

        <Field label="Sub-community"><input className={inputCls} value={form.sub_community || ""} onChange={(e) => update("sub_community", e.target.value)} /></Field>
        <Field label="City"><input className={inputCls} value={form.city} onChange={(e) => update("city", e.target.value)} /></Field>

        <Field label="Status">
          <select className={inputCls} data-testid="project-input-status" value={form.status} onChange={(e) => update("status", e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Handover date"><input className={inputCls} value={form.handover || ""} onChange={(e) => update("handover", e.target.value)} placeholder="e.g. Q4 2027" /></Field>

        <Field label="Starting price (AED)"><input type="number" className={inputCls} data-testid="project-input-price" value={form.starting_price} onChange={(e) => update("starting_price", e.target.value)} /></Field>
        <Field label="Payment plan"><input className={inputCls} value={form.payment_plan || ""} onChange={(e) => update("payment_plan", e.target.value)} placeholder="20 / 40 / 40" /></Field>

        <Field label="Latitude"><input type="number" step="0.0001" className={inputCls} data-testid="project-input-lat" value={form.latitude} onChange={(e) => update("latitude", e.target.value)} /></Field>
        <Field label="Longitude"><input type="number" step="0.0001" className={inputCls} data-testid="project-input-lng" value={form.longitude} onChange={(e) => update("longitude", e.target.value)} /></Field>

        <div className="md:col-span-2">
          <Field label="Unit types (comma-separated)"><CsvInput className={inputCls} value={form.unit_types} onChange={(v) => update("unit_types", v)} placeholder="Studio, 1BR, 2BR, 3BR" data-testid="project-input-unit-types" /></Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Amenities (comma-separated)"><CsvInput className={inputCls} value={form.amenities} onChange={(v) => update("amenities", v)} placeholder="Infinity pool, Gym, Concierge" data-testid="project-input-amenities" /></Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Nearby landmarks (comma-separated)"><CsvInput className={inputCls} value={form.nearby_landmarks} onChange={(v) => update("nearby_landmarks", v)} data-testid="project-input-landmarks" /></Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Description">
            <textarea className={inputCls} rows={5} value={form.description || ""} onChange={(e) => update("description", e.target.value)} />
          </Field>
        </div>

        <Field label="Hero image URL"><input className={inputCls} value={form.hero_image || ""} onChange={(e) => update("hero_image", e.target.value)} /></Field>
        <Field label="Brochure URL"><input className={inputCls} value={form.brochure_url || ""} onChange={(e) => update("brochure_url", e.target.value)} /></Field>

        <div className="md:col-span-2">
          <Field label="Gallery image URLs (comma-separated)"><CsvInput className={inputCls} value={form.gallery} onChange={(v) => update("gallery", v)} data-testid="project-input-gallery" /></Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Floor plan URLs (comma-separated)"><CsvInput className={inputCls} value={form.floor_plans} onChange={(v) => update("floor_plans", v)} data-testid="project-input-floorplans" /></Field>
        </div>

        <Field label="SEO title"><input className={inputCls} value={form.seo_title || ""} onChange={(e) => update("seo_title", e.target.value)} /></Field>
        <Field label="SEO description"><input className={inputCls} value={form.seo_description || ""} onChange={(e) => update("seo_description", e.target.value)} /></Field>

        <div className="md:col-span-2">
          <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">FAQs</span>
          <div className="space-y-2 mb-3">
            {(form.faqs || []).map((f, i) => (
              <div key={i} className="border border-[#E5E7EB] p-3 bg-gray-50">
                <div className="text-sm font-medium">{f.q}</div>
                <div className="text-xs text-gray-600 mt-1">{f.a}</div>
                <button type="button" onClick={() => update("faqs", form.faqs.filter((_, idx) => idx !== i))} className="text-xs text-red-600 mt-2">Remove</button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input className={inputCls} placeholder="Question" value={faqDraft.q} onChange={(e) => setFaqDraft({ ...faqDraft, q: e.target.value })} />
            <input className={inputCls} placeholder="Answer" value={faqDraft.a} onChange={(e) => setFaqDraft({ ...faqDraft, a: e.target.value })} />
          </div>
          <button
            type="button"
            className="mt-2 text-xs px-3 py-2 bg-white border border-[#001588] text-[#001588] uppercase tracking-[0.2em]"
            onClick={() => {
              if (!faqDraft.q || !faqDraft.a) return;
              update("faqs", [...(form.faqs || []), faqDraft]);
              setFaqDraft({ q: "", a: "" });
            }}
          >+ Add FAQ</button>
        </div>

        <Field label="Publish status">
          <select className={inputCls} data-testid="project-input-publish" value={form.publish_status} onChange={(e) => update("publish_status", e.target.value)}>
            {PUBLISH.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 self-end pb-2">
          <input type="checkbox" checked={form.featured} onChange={(e) => update("featured", e.target.checked)} />
          <span className="text-sm">Featured</span>
        </label>
      </div>
    </form>
  );
}
